// Diamonds → USD calculator (public). Adapté de Black OS sur les primitives
// NoiRock. Modélise le programme TikTok "Evolutive LIVE Rewards" (38–53%).
// Net créateur ≈ diamonds × $0.01 × (reward% / 100). Autonome (pas de DB). i18n.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../../utils/creatorLang';
import {
  CreatorScreen, ScreenHeader, Card, SectionLabel, StatTile, BottomNav, I,
} from '../../components/creator/primitives';
import { PublicSettingsButton } from '../../components/creator/PublicSettingsButton';

const GROSS_USD_PER_DIAMOND = 0.01;
const REWARD_MIN_PCT = 38;
const REWARD_MAX_PCT = 53;
const REWARD_DEFAULT_PCT = 46;
const DIAMOND_PRESETS = [1_000, 10_000, 100_000, 500_000, 1_000_000];

const fmtUsd = (v) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n).toLocaleString('en-US');

const Calculator = () => {
  const navigate = useNavigate();
  const [diamonds, setDiamonds] = useState(10_000);
  const [rewardPct, setRewardPct] = useState(REWARD_DEFAULT_PCT);

  const usd = useMemo(() => diamonds * GROSS_USD_PER_DIAMOND * (rewardPct / 100), [diamonds, rewardPct]);
  const monthEst = useMemo(() => usd * 30 / 7, [usd]);
  const minUsd = diamonds * GROSS_USD_PER_DIAMOND * (REWARD_MIN_PCT / 100);
  const maxUsd = diamonds * GROSS_USD_PER_DIAMOND * (REWARD_MAX_PCT / 100);

  return (
    <CreatorScreen>
      <ScreenHeader title={t('pub_calc_title')} subtitle={t('pub_calc_subtitle')} onBack={() => navigate('/my/public-dashboard')} right={<PublicSettingsButton />} />

      <div style={{ padding: '16px 20px 96px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Diamonds input */}
        <Card pad={16}>
          <SectionLabel>{t('pub_calc_diamonds')}</SectionLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <I.Diamond size={18} stroke="var(--accent-cyan)" />
            <input
              type="number" value={diamonds} min={0} step={1000}
              onChange={(e) => setDiamonds(Math.max(0, Number(e.target.value) || 0))}
              style={{ flex: 1, fontSize: 18, fontWeight: 700, padding: '10px 12px', borderRadius: 10, background: 'var(--bg-3)', border: '1px solid var(--line-1)', color: 'var(--fg-0)', fontFamily: 'var(--font-mono)' }}
            />
          </div>
          <input type="range" min={0} max={1_000_000} step={1000} value={diamonds} onChange={(e) => setDiamonds(Number(e.target.value))} style={{ width: '100%', accentColor: 'var(--accent-cyan)' }} />
          <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
            {DIAMOND_PRESETS.map((p) => (
              <button key={p} onClick={() => setDiamonds(p)} style={{ padding: '6px 12px', borderRadius: 999, background: diamonds === p ? 'var(--accent-cyan)' : 'var(--bg-3)', color: diamonds === p ? 'var(--bg-0)' : 'var(--fg-2)', border: '1px solid var(--line-1)', fontSize: 11, fontWeight: 600 }}>
                {p >= 1_000_000 ? `${p / 1_000_000}M` : `${p / 1_000}k`}
              </button>
            ))}
          </div>
        </Card>

        {/* Reward % */}
        <Card pad={16}>
          <SectionLabel>{t('pub_calc_reward')}</SectionLabel>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 32, fontWeight: 700, color: 'var(--fg-0)', fontFamily: 'var(--font-mono)', letterSpacing: -0.8 }}>{rewardPct}%</span>
            <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('pub_calc_of')} {fmtUsd(diamonds * GROSS_USD_PER_DIAMOND)}</span>
          </div>
          <input type="range" min={REWARD_MIN_PCT} max={REWARD_MAX_PCT} step={1} value={rewardPct} onChange={(e) => setRewardPct(Number(e.target.value))} style={{ width: '100%', accentColor: 'var(--accent-pink)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>{REWARD_MIN_PCT}% · {t('pub_calc_baseline')}</span>
            <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>{REWARD_MAX_PCT}% · {t('pub_calc_maxed')}</span>
          </div>
        </Card>

        {/* Results */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          <StatTile label={t('pub_calc_perweek')} value={fmtUsd(usd)} />
          <StatTile label={t('pub_calc_permonth')} value={fmtUsd(monthEst)} />
        </div>

        {/* Range */}
        <Card pad={14} style={{ borderColor: 'var(--accent-pink)' }}>
          <SectionLabel>{t('pub_calc_range')}</SectionLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--fg-1)' }}>
            <span style={{ fontWeight: 700, color: 'var(--fg-2)' }}>{fmtUsd(minUsd)}</span>
            <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>({REWARD_MIN_PCT}%)</span>
            <I.Chevron size={12} stroke="var(--fg-3)" />
            <span style={{ fontWeight: 700, color: 'var(--accent-pink)' }}>{fmtUsd(maxUsd)}</span>
            <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>({REWARD_MAX_PCT}%)</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 6, lineHeight: 1.5 }}>{t('pub_calc_range_desc')}</div>
        </Card>

        {/* Agency tiers (éducatif) */}
        <Card pad={16}>
          <SectionLabel>{t('pub_calc_tiers')}</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
            <TierRow label="Junior" min={0} max={10_000} color="#94a3b8" />
            <TierRow label="Rising" min={10_000} max={100_000} color="#22c55e" />
            <TierRow label="Senior" min={100_000} max={500_000} color="#f59e0b" />
            <TierRow label="Elite" min={500_000} max={null} color="#a855f7" />
          </div>
        </Card>

        <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--bg-2)', border: '1px solid var(--line-1)', fontSize: 11, color: 'var(--fg-3)', lineHeight: 1.5 }}>
          <I.Info size={12} stroke="currentColor" style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {t('pub_calc_disclaimer')}
        </div>

        <button onClick={() => navigate('/my/apply')} style={{ padding: '14px 16px', borderRadius: 12, background: 'var(--accent-pink)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <I.Sparkle size={14} stroke="currentColor" /> {t('pub_calc_cta')}
        </button>
      </div>

      <BottomNav active="calculator" variant="public" />
    </CreatorScreen>
  );
};

function TierRow({ label, min, max, color }) {
  const range = max ? `${fmtNum(min)} – ${fmtNum(max)}` : `${fmtNum(min)}+`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-0)', minWidth: 56 }}>{label}</span>
      <span style={{ flex: 1, fontSize: 11, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)' }}>
        {range} <I.Diamond size={10} stroke="currentColor" style={{ verticalAlign: 'middle' }} />
      </span>
    </div>
  );
}

export default Calculator;
